// src/hooks/usePosts.ts
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PostsApi, CreatePostPayload, UpdatePostPayload, Post } from '@/services/api';
import { useAuth } from '@/auth/AuthProvider';
import { toast } from 'sonner';
import { useToast } from '@/hooks/useToast';
import { verifyPlatformConnection, getPlatformDisplayName } from '@/utils/accountUtils';

// Polling interval for full data refresh
const DEFAULT_REFETCH_INTERVAL = 15000; // 15 seconds

// Posts query key
export const postsQueryKey = ['posts'];

/**
 * Custom hook for fetching and managing posts.
 * Uses polling to regularly fetch updates.
 */
export const usePosts = (options = {}) => {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  
  // Regular polling settings
  const staleTime = 30000; // 30 seconds
  
  // Fetch posts query
  const query = useQuery({
    queryKey: postsQueryKey,
    queryFn: async () => {
      // fetchWithRetry handles retries, refreshes, and throws standard errors on failure.
      // PostsApi.getPosts returns the raw Response object.
      const response = await PostsApi.getPosts();
      
      // If status is 304, return the existing data from the cache.
      if (response.status === 304) {
        return queryClient.getQueryData(postsQueryKey) || []; // Return cached data or empty array
      }
      
      // If response is OK (200), parse and return the posts data.
      if (response.ok) {
        return await response.json(); // Should return Post[]
      }
      
      // If we get here, something unexpected happened (e.g., fetchWithRetry failed to handle an error).
      // Throw an error to put the query in an error state.
      console.error('usePosts (queryFn): Received unexpected response status after fetchWithRetry:', response.status);
      throw new Error(`Failed to fetch posts: Status ${response.status}`);
    },
    enabled: isAuthenticated,
    refetchInterval: DEFAULT_REFETCH_INTERVAL,
    staleTime,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options
  });
  
  // Only force refetch on initial authentication
  useEffect(() => {
    // Use a ref to track if this is the first auth change
    const isInitialAuth = !query.data && isAuthenticated && user;
    
    if (isInitialAuth) {
      // Refetch only once when first authenticated
      query.refetch();
    }
  }, [isAuthenticated, user, query]);
  
  // Clear posts when user logs out
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Clear cache silently - no need to log
      queryClient.resetQueries({ queryKey: postsQueryKey });
    }
  }, [isAuthenticated, user, queryClient]);
  
  return query;
};

/**
 * Custom hook for creating a new post
 */
export const useCreatePost = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (post: CreatePostPayload) => {
      // Verify that the user has credentials for the selected platform
      const isConnected = await verifyPlatformConnection(post.platform);
      
      if (!isConnected) {
        throw new Error(`You don't have a connected ${getPlatformDisplayName(post.platform)} account. Please go to your Profile to connect it.`);
      }
      
      return PostsApi.createPost(post);
    },
    onSuccess: () => {
      toast({
        title: 'Post created successfully',
        description: 'Your post has been scheduled',
        type: 'success'
      });
      // Only invalidate if the user is authenticated
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: postsQueryKey });
      }
    },
    onError: (error) => {
      console.error('Error creating post:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      toast({
        title: 'Failed to create post',
        description: errorMessage,
        type: 'error'
      });
    }
  });
};

/**
 * Custom hook for updating an existing post
 */
export const useUpdatePost = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  return useMutation<
    Post,
    Error,
    { id: string; post: UpdatePostPayload }
  >({
    mutationFn: async ({ id, post }: { id: string; post: UpdatePostPayload }) => {
      // If the platform is being changed, verify credentials for the new platform
      if (post.platform) {
        const isConnected = await verifyPlatformConnection(post.platform);
        
        if (!isConnected) {
          throw new Error(`You don't have a connected ${getPlatformDisplayName(post.platform)} account. Please go to your Profile to connect it.`);
        }
      }
      
      return PostsApi.updatePost(id, post);
    },
    onSuccess: (updatedPost) => {
      toast({
        title: 'Post updated successfully',
        type: 'success'
      });
      // Only invalidate if the user is authenticated
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: postsQueryKey });
      }
      
      // Optionally update the specific post in the cache immediately
      queryClient.setQueryData(['posts', updatedPost.id], updatedPost);
      
      // Could also update the main 'posts' list cache optimistically
      queryClient.setQueryData<Post[]>(['posts'], (oldData) => 
        oldData ? oldData.map(p => p.id === updatedPost.id ? updatedPost : p) : []
      );
    },
    onError: (error) => {
      console.error('Error updating post:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      toast({
        title: 'Failed to update post',
        description: errorMessage,
        type: 'error'
      });
    }
  });
};

/**
 * Custom hook for deleting a post
 */
export const useDeletePost = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  
  return useMutation<
    void,
    Error,
    string
  >({
    mutationFn: (id: string) => PostsApi.deletePost(id),
    onSuccess: (_, deletedPostId) => {
      toast.success('Post deleted successfully');
      // Only invalidate if the user is authenticated
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: postsQueryKey });
      }
      
      // Optimistically remove the post from the cache
      queryClient.setQueryData<Post[]>(['posts'], (oldData) =>
        oldData ? oldData.filter(p => p.id !== deletedPostId) : []
      );
    },
    onError: (error) => {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  });
};

/**
 * Custom hook for posting a scheduled post immediately
 */
export const usePostNow = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  return useMutation<
    Post,
    Error,
    string
  >({
    mutationFn: (id: string) => PostsApi.postNow(id),
    onSuccess: (result) => {
      toast({
        title: 'Post submitted successfully',
        description: 'Your post is being processed for immediate publication',
        type: 'success'
      });
      // Only invalidate if the user is authenticated
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: postsQueryKey });
      }
      
      // Optimistically update the specific post in the cache 
      // Use result which contains the updated post status and potentially platform post_id
      queryClient.setQueryData<Post[]>(['posts'], (oldData) => 
        oldData ? oldData.map(p => p.id === result.id ? result : p) : []
      );
    },
    onError: (error) => {
      console.error('Error posting now:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: 'Failed to post immediately',
        description: errorMessage,
        type: 'error'
      });
    }
  });
};